import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Fab,
} from '@mui/material';
import {
  Home,
  Analytics,
  Create,
  Notifications,
  Person,
  Add,
  Search,
  Settings,
  Refresh,
} from '@mui/icons-material';

// Mobile-specific hooks
import { usePlatform } from '../../hooks/shared/usePlatform';
import { useHapticFeedback } from '../../hooks/mobile/useHapticFeedback';

// Mobile components
import MobileDashboard from './MobileDashboard';

/**
 * Mobile-specific layout component
 * Provides mobile-optimized navigation and layout
 */
const MobileLayout = ({ isAuthenticated, children }) => {
  const { isMobile } = usePlatform();
  const { hapticButton, hapticNavigation } = useHapticFeedback();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  // Navigation tabs
  const tabs = [
    { label: 'Home', icon: <Home />, component: <MobileDashboard /> },
    { label: 'Analytics', icon: <Analytics />, component: <div>Analytics</div> },
    { label: 'Create', icon: <Create />, component: <div>Create</div> },
    { label: 'Notifications', icon: <Notifications />, component: <div>Notifications</div> },
    { label: 'Profile', icon: <Person />, component: <div>Profile</div> },
  ];

  // Speed dial actions
  const speedDialActions = [
    { icon: <Add />, name: 'New Post', action: () => hapticButton(() => console.log('New Post'), 'medium')() },
    { icon: <Search />, name: 'Search', action: () => hapticButton(() => console.log('Search'), 'medium')() },
    { icon: <Refresh />, name: 'Refresh', action: () => hapticButton(() => console.log('Refresh'), 'medium')() },
    { icon: <Settings />, name: 'Settings', action: () => hapticButton(() => console.log('Settings'), 'medium')() },
  ];

  // Handle tab change with haptic feedback
  const handleTabChange = (event, newValue) => {
    hapticNavigation();
    setCurrentTab(newValue);
  };

  // Handle speed dial toggle
  const handleSpeedDialToggle = () => {
    hapticButton(() => setSpeedDialOpen(!speedDialOpen), 'light')();
  };

  if (!isMobile) {
    return null; // Only render on mobile
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Social Media Tool
          </Typography>
          <IconButton 
            color="inherit"
            onClick={hapticButton(() => console.log('Notifications'), 'light')}
          >
            <Notifications />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          pt: 7, // Space for AppBar
          pb: 7, // Space for BottomNavigation
          overflow: 'auto',
          backgroundColor: 'background.default',
        }}
      >
        {tabs[currentTab].component}
      </Box>

      {/* Bottom Navigation */}
      <BottomNavigation
        value={currentTab}
        onChange={handleTabChange}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1000,
        }}
      >
        {tabs.map((tab, index) => (
          <BottomNavigationAction
            key={tab.label}
            label={tab.label}
            icon={tab.icon}
            sx={{
              '&.Mui-selected': {
                color: 'primary.main',
              },
            }}
          />
        ))}
      </BottomNavigation>

      {/* Speed Dial */}
      <SpeedDial
        ariaLabel="Quick Actions"
        sx={{ 
          position: 'fixed', 
          bottom: 80, 
          right: 16,
          zIndex: 1001,
        }}
        icon={<SpeedDialIcon />}
        open={speedDialOpen}
        onOpen={handleSpeedDialToggle}
        onClose={handleSpeedDialToggle}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={action.action}
          />
        ))}
      </SpeedDial>
    </Box>
  );
};

export default MobileLayout;
